import React, { useEffect, useMemo } from 'react'
import {
  ColDef,
  GridOptions,
  IDetailCellRendererParams,
} from 'ag-grid-community'
import { useLazyGetGroupListQuery } from '../../../apis/firm-management/firm-management-api.api'
import type {
  FirmData,
  GroupListItem,
  GroupListRequest,
} from '../../../apis/firm-management/firm-management-api.def'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'
import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { useAppSelector } from '../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { StateCell } from './state-cell.tsx'
import { useTranslation } from 'react-i18next'
import { IdeyaButton } from '../../../common/antd/ideya-button/ideya-button.tsx'
import { EditOutlined } from '@ant-design/icons'
import { CustomCellRendererProps } from 'ag-grid-react'
import { useIsSuperViewer } from '../../../common/hooks/use-is-superviewer.ts'
import { useIsMobile } from '../../../common/hooks/use-is-mobile.ts'

type GroupQueryBase = Omit<GroupListRequest, 'firmId'>

interface GroupDetailRendererProps extends IDetailCellRendererParams<FirmData> {
  groupQueryBase?: GroupQueryBase
  onClick: () => void
  onEditGroup: (id: string, firmId: string) => void
}

const GroupDetailRenderer: React.FC<GroupDetailRendererProps> = ({
  data: firm,
  onClick,
  groupQueryBase = { status: 'all', page: 1 },
  onEditGroup,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const [trigger, { data, isFetching, isError, error }] =
    useLazyGetGroupListQuery()
  const isSuperViewer = useIsSuperViewer()
  const isMobile = useIsMobile(768)

  useEffect(() => {
    if (firm?.id) {
      trigger({ firmId: firm.id, ...groupQueryBase })
    }
  }, [firm?.id, groupQueryBase, trigger])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  )

  const groups: GroupListItem[] = data?.data ?? []

  const errorMessage =
    error &&
    ('status' in error
      ? (() => {
          const payload = (error as FetchBaseQueryError).data as
            | { message?: string }
            | string
            | undefined
          return typeof payload === 'string'
            ? payload
            : payload?.message || 'Failed to load groups'
        })()
      : (error as SerializedError).message || 'Failed to load groups')

  if (isError) return <div style={{ color: 'red' }}>{errorMessage}</div>

  const CreateGroupButton = () => {
    return (
      <IdeyaButton ghost onClick={onClick}>
        {t('firm_management.group_modal.create_group')}
      </IdeyaButton>
    )
  }

  const columns: ColDef[] = [
    ...(!isSuperViewer
      ? [
          {
            headerName: '',
            field: 'id',
            width: 50,
            cellRenderer: (props: CustomCellRendererProps) => (
              <IdeyaButton
                ghost
                icon={<EditOutlined />}
                onClick={() => {
                  if (firm?.id) {
                    onEditGroup(props.data.id, firm.id)
                  }
                }}
              />
            ),
          },
        ]
      : []),
    {
      headerName: t('firm_management.group_table.columns.group_name'),
      field: 'name',
    },
    {
      headerName: t('firm_management.group_table.columns.region'),
      field: 'region',
    },
    {
      headerName: t('firm_management.group_table.columns.netCategory'),
      field: 'netCategory',
    },
    {
      headerName: t('firm_management.group_table.columns.state'),
      field: 'isActive',
      cellRenderer: StateCell,
      cellRendererParams: (params: { value: boolean }) => ({
        isActive: Boolean(params.value),
      }),
    },
    ...(!isSuperViewer
      ? [
          {
            headerName: t('firm_management.group_table.columns.action'),
            cellRenderer: CreateGroupButton,
            spanRows: true,
            autoHeight: true,
            cellStyle: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          },
        ]
      : []),
  ]

  // Important: no fixed height; let the inner grid grow with content
  const innerGridOptions: GridOptions<GroupListItem> = {
    domLayout: 'autoHeight',
  }

  return (
    <div
      style={{
        padding: `${themeStyle.paddingBase} ${themeStyle.paddingBase} ${themeStyle.paddingBase} 42px`,
      }}
    >
      <IdeyaAgGridTable
        defaultColDef={defaultColDef}
        loading={isFetching}
        columns={columns}
        rowData={groups}
        height={'100%'}
        gridOptions={innerGridOptions}
        enableCellSpan={true}
        autoSizeStrategy={isMobile ? undefined : { type: 'fitGridWidth' }}
      />
    </div>
  )
}

export default GroupDetailRenderer
